import {
  useHasPermission,
  useHasProfile,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { PROFILES, PERMISSIONS } from "./constants";
import { ProfileCapabilities } from "../../models";

export const useProfiles = () => {
  const hasProfile = useHasProfile();
  const hasPermission = useHasPermission();
  const { state } = useUTLSettingsContext();

  // Get operator billet limit from UTL settings with fallback to hardcoded constant
  const operatorBilletLimit = parseInt(
    get(
      state.settings,
      "BYI_APP_OPERATOR_MAX_BILLETS",
      PERMISSIONS.OPERATOR_BILLET_MOVE_LIMIT,
    ),
  );

  // Role-based profile checks (what they can do)
  const isOperator = hasProfile(PROFILES.OPERATOR); // billetyardinv_read_operate
  const isSupervisor = hasProfile(PROFILES.SUPERVISOR); // billetyardinv_read_supervise
  const hasMESSupervisorRole = hasProfile(PROFILES.MES_SUPERVISOR); // MESBYISupervisorRole
  const isReadOnly = hasProfile(PROFILES.READ_ONLY);

  // Access mode profile checks (how they access the system)
  const isTouchMode = hasProfile(PROFILES.TOUCH_ACCESS); // DisplayedOnMES=0 behavior
  const isMESMode = hasProfile(PROFILES.MES_ACCESS); // DisplayedOnMES=1 behavior

  // Access mode capabilities
  const shouldBypassCraneSelection = isMESMode || isSupervisor; // MES mode or supervisors bypass crane selection
  const canManageCranes = isTouchMode && isOperator; // Only touch mode operators need crane selection
  const showLogoutButton = isTouchMode; // Only show logout in touch mode
  const showLoginButton = isTouchMode && !isSupervisor; // Only touch mode non-supervisors need login

  // Role-based capabilities (unchanged)
  const canBypassBilletLimit = isSupervisor; // BYI Supervisor can bypass (unlimited moves)
  const canClearProtectedLocations = hasMESSupervisorRole; // Only BYI-MES Supervisor can clear protected locations
  const canAccessLocationContentEdition = isSupervisor; // BYI Supervisor can edit content (with or without MES role)
  const canOpenMoveBilletsModal = isOperator || isSupervisor; // All except read-only can move billets

  // Role capabilities object
  const capabilities: ProfileCapabilities = {
    isOperator,
    isSupervisor,
    isReadOnly,
    canBypassBilletLimit,
    canClearProtectedLocations,
    canOpenMoveBilletsModal,
    maxBilletsPerMove: canBypassBilletLimit ? null : operatorBilletLimit,
  };

  /**
   * Check if user can move specified number of billets
   * @param billetCount Number of billets to move
   * @returns Object with validation result and message
   */
  const validateBilletMove = (billetCount: number) => {
    if (canBypassBilletLimit) {
      return {
        isValid: true,
        canProceed: true,
        message: null,
        isWarning: false,
      };
    }

    if (billetCount <= operatorBilletLimit) {
      return {
        isValid: true,
        canProceed: true,
        message: null,
        isWarning: false,
      };
    }

    // Operator exceeding limit - show warning but allow operation
    return {
      isValid: false,
      canProceed: true, // Legacy behavior: allow with warning
      message: `The limit of billets to move is ${operatorBilletLimit}.`,
      isWarning: true,
    };
  };

  /**
   * Map old system roles to new profile checks
   * @param oldRole The role from ClearRequiredUserRole column
   * @returns boolean indicating if current user has equivalent new profile
   */
  const mapOldRoleToNewProfile = (oldRole: string): boolean => {
    switch (oldRole.trim()) {
      case "MESBYISupervisorRole":
        // Old MESBYISupervisorRole maps directly to new BYI-MES Supervisor profile
        return hasMESSupervisorRole;
      case "billetyardinv_read_operate":
        // Old operator role maps directly to new BYI Operator profile
        return isOperator;
      case "billetyardinv_read_supervise":
        // Old supervisor role maps directly to new BYI Supervisor profile
        return isSupervisor;
      default:
        // Try hasPermission for any unmapped roles (fallback)
        return hasPermission(oldRole);
    }
  };

  /**
   * Check if user can clear a specific location
   * @param location Location object with ClearRequiredUserRole property
   * @returns boolean indicating if clear operation is allowed
   */
  const canClearLocation = (location: {
    ClearEnabled?: boolean;
    ClearRequiredUserRole?: string;
  }): boolean => {
    if (!location.ClearEnabled) {
      return false;
    }

    // No specific role required
    if (
      !location.ClearRequiredUserRole ||
      location.ClearRequiredUserRole.trim() === ""
    ) {
      return true;
    }

    // Map old system role to new profile check
    return mapOldRoleToNewProfile(location.ClearRequiredUserRole);
  };

  /**
   * Get user's role level for display purposes
   * @returns User-friendly role description
   */
  const getRoleLevel = (): string => {
    if (isSupervisor && hasMESSupervisorRole) {
      return "Full Supervisor"; // BYI Supervisor + BYI-MES Supervisor
    }
    if (isSupervisor) {
      return "Supervisor"; // BYI Supervisor only
    }
    if (isOperator) {
      return "Operator"; // BYI Operator
    }
    if (isReadOnly) {
      return "Read Only"; // BYI Read Only
    }
    return "Unknown";
  };

  /**
   * Check if current user has any  access
   * @returns boolean indicating if user has  permissions
   */
  const hasAccess = (): boolean => {
    return isOperator || isSupervisor || hasMESSupervisorRole || isReadOnly;
  };

  return {
    // Role flags
    isOperator,
    isSupervisor,
    hasMESSupervisorRole,
    isReadOnly,

    // Access mode flags
    isTouchMode,
    isMESMode,

    // Role-based capabilities
    capabilities,
    canBypassBilletLimit,
    canClearProtectedLocations,
    canAccessLocationContentEdition,
    canOpenMoveBilletsModal,

    // Access mode capabilities
    shouldBypassCraneSelection,
    canManageCranes,
    showLogoutButton,
    showLoginButton,

    // Utility functions
    validateBilletMove,
    canClearLocation,
    getRoleLevel,
    hasAccess,

    // Raw permission checker (for custom checks)
    hasPermission,
  };
};
